import React, { useEffect, useState } from 'react';
import { UploadIcon, CheckCircleIcon, AlertCircleIcon, SearchIcon, PlusIcon, XIcon, ChevronLeftIcon, ChevronRightIcon, CheckIcon, EyeIcon, EyeOffIcon, FilterIcon, XCircleIcon, RefreshCwIcon, DownloadIcon } from 'lucide-react';
type Certificate = {
  id: string;
  participant: string;
  bic: string;
  status: 'Active' | 'Inactive';
  validFrom: string;
  validTo: string;
  serialNo: string;
  lastUpdated: string;
};
type Tab = 'list' | 'add';
export function CertificateManagement() {
  // Tab state
  const [activeTab, setActiveTab] = useState<Tab>('list');
  // List view states
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [showFilters, setShowFilters] = useState(false);
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [sortField, setSortField] = useState<keyof Certificate | ''>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  // Form states
  const [certificateText, setCertificateText] = useState('');
  const [privateKeyText, setPrivateKeyText] = useState('');
  const [privateKeyPassword, setPrivateKeyPassword] = useState('');
  const [keystorePassword, setKeystorePassword] = useState('');
  const [showPrivateKeyPassword, setShowPrivateKeyPassword] = useState(false);
  const [showKeystorePassword, setShowKeystorePassword] = useState(false);
  // Certificate metadata after parsing
  const [certificateMetadata, setCertificateMetadata] = useState<{
    subject: string;
    issuer: string;
    serial: string;
    validFrom: string;
    validTo: string;
    isValid: boolean;
  } | null>(null);
  // Validation states
  const [errors, setErrors] = useState<{
    certificate?: string;
    privateKey?: string;
    privateKeyPassword?: string;
    keystorePassword?: string;
    keyMatch?: string;
  }>({});
  // Modal states
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [showActivateModal, setShowActivateModal] = useState(false);
  const [selectedCertificate, setSelectedCertificate] = useState<Certificate | null>(null);
  const [showSuccessBanner, setShowSuccessBanner] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  // Bank information - hardcoded for this bank portal
  const bankInfo = {
    code: 'AMHRETAA',
    name: 'Amhara Bank S.C.'
  };
  // Sample certificates data - only showing certificates for the login bank (Amhara Bank)
  const [certificates, setCertificates] = useState<Certificate[]>([{
    id: '1',
    participant: 'EthSwitch',
    bic: 'ETSWETAA',
    status: 'Active',
    validFrom: '2023-01-15',
    validTo: '2024-01-15',
    serialNo: '12:AB:CD:34:EF:56',
    lastUpdated: '2023-01-15 09:30:45'
  }, {
    id: '10',
    participant: 'Amhara Bank',
    bic: 'AMHRETAA',
    status: 'Active',
    validFrom: '2023-10-15',
    validTo: '2024-10-15',
    serialNo: '78:9A:BC:DE:F0:12',
    lastUpdated: '2023-10-15 08:55:30'
  }]);
  // Filter and sort certificates
  const filteredCertificates = certificates.filter(cert => {
    // Apply search filter
    const searchMatch = cert.participant.toLowerCase().includes(searchTerm.toLowerCase()) || cert.bic.toLowerCase().includes(searchTerm.toLowerCase()) || cert.serialNo.toLowerCase().includes(searchTerm.toLowerCase());
    // Apply status filter
    const statusMatch = statusFilter === 'all' || statusFilter === 'active' && cert.status === 'Active' || statusFilter === 'inactive' && cert.status === 'Inactive';
    return searchMatch && statusMatch;
  });
  // Sort certificates
  const sortedCertificates = [...filteredCertificates].sort((a, b) => {
    if (!sortField) return 0;
    const aValue = a[sortField];
    const bValue = b[sortField];
    if (sortDirection === 'asc') {
      return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
    } else {
      return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
    }
  });
  // Paginate certificates
  const paginatedCertificates = sortedCertificates.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  const totalPages = Math.ceil(filteredCertificates.length / itemsPerPage);
  // Handle sort
  const handleSort = (field: keyof Certificate) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };
  // Handle status change
  const handleStatusChange = (certificate: Certificate) => {
    if (certificate.status === 'Active') {
      setSelectedCertificate(certificate);
      setShowDeactivateModal(true);
    } else {
      setSelectedCertificate(certificate);
      setShowActivateModal(true);
    }
  };
  // Confirm status change
  const confirmStatusChange = () => {
    if (!selectedCertificate) return;
    const newStatus = selectedCertificate.status === 'Active' ? 'Inactive' : 'Active';
    const updatedCertificates = certificates.map(cert => cert.id === selectedCertificate.id ? {
      ...cert,
      status: newStatus,
      lastUpdated: new Date().toISOString().replace('T', ' ').substring(0, 19)
    } : cert);
    setCertificates(updatedCertificates);
    setShowDeactivateModal(false);
    setShowActivateModal(false);
    // Show success banner
    setSuccessMessage(`Certificate ${newStatus === 'Active' ? 'activated' : 'deactivated'} successfully`);
    setShowSuccessBanner(true);
    // Hide success banner after 5 seconds
    setTimeout(() => {
      setShowSuccessBanner(false);
    }, 5000);
  };
  // Simulate certificate parsing
  const parseCertificate = (certText: string) => {
    if (!certText.includes('-----BEGIN CERTIFICATE-----')) {
      setErrors({
        ...errors,
        certificate: 'Certificate parsing failed. Expecting PEM with BEGIN CERTIFICATE.'
      });
      setCertificateMetadata(null);
      return;
    }
    // Clear certificate error
    const newErrors = {
      ...errors
    };
    delete newErrors.certificate;
    setErrors(newErrors);
    // Simulate parsing and extract metadata
    // In a real app, this would use a proper certificate parsing library
    setTimeout(() => {
      const today = new Date();
      const validFrom = new Date(today);
      validFrom.setMonth(today.getMonth() - 1);
      const validTo = new Date(today);
      validTo.setFullYear(today.getFullYear() + 1);
      setCertificateMetadata({
        subject: `CN=${bankInfo.name}, O=EthSwitch, C=ET`,
        issuer: 'CN=EthSwitch Certificate Authority, O=EthSwitch, C=ET',
        serial: Array.from({
          length: 6
        }, () => Math.floor(Math.random() * 256).toString(16).padStart(2, '0')).join(':').toUpperCase(),
        validFrom: validFrom.toISOString().split('T')[0],
        validTo: validTo.toISOString().split('T')[0],
        isValid: true
      });
    }, 500);
  };
  // Handle certificate upload
  const handleCertificateUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = e => {
      const content = e.target?.result as string;
      setCertificateText(content);
      parseCertificate(content);
    };
    reader.readAsText(file);
  };
  // Handle private key upload
  const handlePrivateKeyUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = e => {
      const content = e.target?.result as string;
      setPrivateKeyText(content);
      // Validate private key
      if (!content.includes('-----BEGIN ENCRYPTED PRIVATE KEY-----')) {
        setErrors({
          ...errors,
          privateKey: 'Private key is unencrypted. Upload an encrypted PKCS#8 key.'
        });
      } else {
        const newErrors = {
          ...errors
        };
        delete newErrors.privateKey;
        setErrors(newErrors);
      }
    };
    reader.readAsText(file);
  };
  // Validate form
  const validateForm = () => {
    const newErrors: typeof errors = {};
    if (!certificateText) {
      newErrors.certificate = 'Certificate is required';
    } else if (!certificateText.includes('-----BEGIN CERTIFICATE-----')) {
      newErrors.certificate = 'Certificate parsing failed. Expecting PEM with BEGIN CERTIFICATE.';
    }
    if (!privateKeyText) {
      newErrors.privateKey = 'Private key is required';
    } else if (!privateKeyText.includes('-----BEGIN ENCRYPTED PRIVATE KEY-----')) {
      newErrors.privateKey = 'Private key is unencrypted. Upload an encrypted PKCS#8 key.';
    }
    if (!privateKeyPassword) {
      newErrors.privateKeyPassword = 'Private key password is required';
    }
    if (!keystorePassword) {
      newErrors.keystorePassword = 'Keystore password is required';
    } else if (keystorePassword.length < 8) {
      newErrors.keystorePassword = 'Keystore password must be at least 8 characters';
    }
    // Simulate public/private key match validation
    // In a real app, this would be a cryptographic check
    if (certificateText && privateKeyText && Math.random() > 0.8) {
      newErrors.keyMatch = 'Certificate public key does not match private key.';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    // Simulate adding a new certificate
    const newCertificate: Certificate = {
      id: (certificates.length + 1).toString(),
      participant: bankInfo.name,
      bic: bankInfo.code,
      status: 'Active',
      validFrom: certificateMetadata?.validFrom || new Date().toISOString().split('T')[0],
      validTo: certificateMetadata?.validTo || new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0],
      serialNo: certificateMetadata?.serial || '00:00:00:00:00:00',
      lastUpdated: new Date().toISOString().replace('T', ' ').substring(0, 19)
    };
    setCertificates([...certificates, newCertificate]);
    // Reset form
    setCertificateText('');
    setPrivateKeyText('');
    setPrivateKeyPassword('');
    setKeystorePassword('');
    setCertificateMetadata(null);
    // Show success banner
    setSuccessMessage('Certificate added successfully');
    setShowSuccessBanner(true);
    // Hide success banner after 5 seconds
    setTimeout(() => {
      setShowSuccessBanner(false);
    }, 5000);
    // Switch to list view
    setActiveTab('list');
  };
  return <div className="space-y-6" data-id="element-1555">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-1556">
        <h1 className="text-2xl font-bold text-[#1A1F36]" data-id="element-1557">
          Certificate Management
        </h1>
        <div className="flex items-center gap-3" data-id="element-1558">
          {activeTab === 'list' ? <button onClick={() => setActiveTab('add')} className="px-3 py-2 bg-[#1A6FEF] text-white rounded-md font-medium hover:bg-[#0F5ED6] flex items-center" data-id="element-1559">
              <PlusIcon size={16} className="mr-2" data-id="element-1560" />
              Add Certificate
            </button> : <button onClick={() => setActiveTab('list')} className="px-3 py-2 bg-gray-100 text-gray-700 rounded-md font-medium hover:bg-gray-200 flex items-center" data-id="element-1561">
              <ChevronLeftIcon size={16} className="mr-2" data-id="element-1562" />
              Back to List
            </button>}
        </div>
      </div>
      {/* Success Banner */}
      {showSuccessBanner && <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-md flex items-center justify-between" data-id="element-1563">
          <div className="flex items-center" data-id="element-1564">
            <CheckCircleIcon size={20} className="text-green-500 mr-2" data-id="element-1565" />
            <span data-id="element-1566">{successMessage}</span>
          </div>
          <button onClick={() => setShowSuccessBanner(false)} className="text-green-500 hover:text-green-700" data-id="element-1567">
            <XIcon size={16} data-id="element-1568" />
          </button>
        </div>}
      {/* Certificate List */}
      {activeTab === 'list' && <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden" data-id="element-1569">
          <div className="p-4 border-b border-gray-200 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-1570">
            <h2 className="font-medium text-[#1A1F36]" data-id="element-1571">Certificate List</h2>
            <div className="flex flex-wrap items-center gap-3" data-id="element-1572">
              <div className="relative" data-id="element-1573">
                <input type="text" placeholder="Search certificates..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1A6FEF] focus:border-transparent" data-id="element-1574" />
                <SearchIcon size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" data-id="element-1575" />
              </div>
              <button onClick={() => setShowFilters(!showFilters)} className={`px-3 py-2 rounded-md font-medium flex items-center ${showFilters ? 'bg-gray-200 text-gray-800' : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'}`} data-id="element-1576">
                <FilterIcon size={16} className="mr-2" data-id="element-1577" />
                Filter
              </button>
              <button onClick={() => {
            setSearchTerm('');
            setStatusFilter('all');
            setSortField('');
            setSortDirection('asc');
          }} className="px-3 py-2 bg-white border border-gray-300 text-gray-700 rounded-md font-medium hover:bg-gray-50 flex items-center" data-id="element-1578">
                <RefreshCwIcon size={16} className="mr-2" data-id="element-1579" />
                Reset
              </button>
            </div>
          </div>
          {/* Filters */}
          {showFilters && <div className="p-4 bg-gray-50 border-b border-gray-200" data-id="element-1580">
              <div className="flex flex-wrap items-center gap-4" data-id="element-1581">
                <div data-id="element-1582">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1583">
                    Status
                  </label>
                  <div className="flex items-center gap-2" data-id="element-1584">
                    <button onClick={() => setStatusFilter('all')} className={`px-3 py-1 text-sm rounded-md ${statusFilter === 'all' ? 'bg-gray-200 text-gray-800' : 'bg-white border border-gray-300 text-gray-700'}`} data-id="element-1585">
                      All
                    </button>
                    <button onClick={() => setStatusFilter('active')} className={`px-3 py-1 text-sm rounded-md ${statusFilter === 'active' ? 'bg-green-100 text-green-800' : 'bg-white border border-gray-300 text-gray-700'}`} data-id="element-1586">
                      Active
                    </button>
                    <button onClick={() => setStatusFilter('inactive')} className={`px-3 py-1 text-sm rounded-md ${statusFilter === 'inactive' ? 'bg-red-100 text-red-800' : 'bg-white border border-gray-300 text-gray-700'}`} data-id="element-1587">
                      Inactive
                    </button>
                  </div>
                </div>
              </div>
            </div>}
          {/* Table */}
          <div className="overflow-x-auto" data-id="element-1588">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-1589">
              <thead className="bg-gray-50" data-id="element-1590">
                <tr data-id="element-1591">
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('participant')} data-id="element-1592">
                    <div className="flex items-center" data-id="element-1593">
                      Participant
                      {sortField === 'participant' && <span className="ml-1" data-id="element-1594">
                          {sortDirection === 'asc' ? '↑' : '↓'}
                        </span>}
                    </div>
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('bic')} data-id="element-1595">
                    <div className="flex items-center" data-id="element-1596">
                      BIC
                      {sortField === 'bic' && <span className="ml-1" data-id="element-1597">
                          {sortDirection === 'asc' ? '↑' : '↓'}
                        </span>}
                    </div>
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('status')} data-id="element-1598">
                    <div className="flex items-center" data-id="element-1599">
                      Status
                      {sortField === 'status' && <span className="ml-1" data-id="element-1600">
                          {sortDirection === 'asc' ? '↑' : '↓'}
                        </span>}
                    </div>
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('validFrom')} data-id="element-1601">
                    <div className="flex items-center" data-id="element-1602">
                      Valid From
                      {sortField === 'validFrom' && <span className="ml-1" data-id="element-1603">
                          {sortDirection === 'asc' ? '↑' : '↓'}
                        </span>}
                    </div>
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('validTo')} data-id="element-1604">
                    <div className="flex items-center" data-id="element-1605">
                      Valid To
                      {sortField === 'validTo' && <span className="ml-1" data-id="element-1606">
                          {sortDirection === 'asc' ? '↑' : '↓'}
                        </span>}
                    </div>
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('serialNo')} data-id="element-1607">
                    <div className="flex items-center" data-id="element-1608">
                      Serial No
                      {sortField === 'serialNo' && <span className="ml-1" data-id="element-1609">
                          {sortDirection === 'asc' ? '↑' : '↓'}
                        </span>}
                    </div>
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('lastUpdated')} data-id="element-1610">
                    <div className="flex items-center" data-id="element-1611">
                      Last Updated
                      {sortField === 'lastUpdated' && <span className="ml-1" data-id="element-1612">
                          {sortDirection === 'asc' ? '↑' : '↓'}
                        </span>}
                    </div>
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1613">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-1614">
                {paginatedCertificates.length > 0 ? paginatedCertificates.map(cert => <tr key={cert.id} className="hover:bg-gray-50" data-id="element-1615">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-[#1A1F36]" data-id="element-1616">
                        {cert.participant}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-1617">
                        {cert.bic}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-1618">
                        <span className={`px-2 py-1 text-xs rounded-full ${cert.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`} data-id="element-1619">
                          {cert.status === 'Active' ? <span className="flex items-center" data-id="element-1620">
                              <CheckCircleIcon size={12} className="mr-1" data-id="element-1621" />
                              {cert.status}
                            </span> : <span className="flex items-center" data-id="element-1622">
                              <XCircleIcon size={12} className="mr-1" data-id="element-1623" />
                              {cert.status}
                            </span>}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-1624">
                        {cert.validFrom}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-1625">
                        {cert.validTo}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-mono" data-id="element-1626">
                        {cert.serialNo}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-1627">
                        {cert.lastUpdated}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1628">
                        <div className="flex items-center space-x-3" data-id="element-1629">
                          <button onClick={() => {}} className="text-blue-600 hover:text-blue-900" title="Modify Certificate" data-id="element-1630">
                            Modify
                          </button>
                          <button onClick={() => handleStatusChange(cert)} className={`${cert.status === 'Active' ? 'text-red-600 hover:text-red-900' : 'text-green-600 hover:text-green-900'}`} title={cert.status === 'Active' ? 'Deactivate Certificate' : 'Activate Certificate'} data-id="element-1631">
                            {cert.status === 'Active' ? 'Deactivate' : 'Activate'}
                          </button>
                          <button onClick={() => {}} className="text-gray-600 hover:text-gray-900" title="Download Certificate" data-id="element-1632">
                            <DownloadIcon size={16} data-id="element-1633" />
                          </button>
                        </div>
                      </td>
                    </tr>) : <tr data-id="element-1634">
                    <td colSpan={8} className="px-6 py-4 text-center text-sm text-gray-500" data-id="element-1635">
                      No certificates found matching your search criteria
                    </td>
                  </tr>}
              </tbody>
            </table>
          </div>
          {/* Pagination */}
          {filteredCertificates.length > 0 && <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between" data-id="element-1636">
              <div className="text-sm text-gray-700" data-id="element-1637">
                Showing{' '}
                <span className="font-medium" data-id="element-1638">
                  {(currentPage - 1) * itemsPerPage + 1}
                </span>{' '}
                to{' '}
                <span className="font-medium" data-id="element-1639">
                  {Math.min(currentPage * itemsPerPage, filteredCertificates.length)}
                </span>{' '}
                of{' '}
                <span className="font-medium" data-id="element-1640">
                  {filteredCertificates.length}
                </span>{' '}
                certificates
              </div>
              <div className="flex items-center space-x-2" data-id="element-1641">
                <button onClick={() => setCurrentPage(Math.max(1, currentPage - 1))} disabled={currentPage === 1} className={`px-2 py-1 rounded-md ${currentPage === 1 ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-100'}`} data-id="element-1642">
                  <ChevronLeftIcon size={18} data-id="element-1643" />
                </button>
                {Array.from({
            length: Math.min(5, totalPages)
          }).map((_, i) => {
            // Show pages around current page
            let pageNum;
            if (totalPages <= 5) {
              pageNum = i + 1;
            } else if (currentPage <= 3) {
              pageNum = i + 1;
            } else if (currentPage >= totalPages - 2) {
              pageNum = totalPages - 4 + i;
            } else {
              pageNum = currentPage - 2 + i;
            }
            return <button key={i} onClick={() => setCurrentPage(pageNum)} className={`px-3 py-1 rounded-md ${currentPage === pageNum ? 'bg-[#1A6FEF] text-white' : 'text-gray-700 hover:bg-gray-100'}`} data-id="element-1644">
                      {pageNum}
                    </button>;
          })}
                <button onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))} disabled={currentPage === totalPages} className={`px-2 py-1 rounded-md ${currentPage === totalPages ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-100'}`} data-id="element-1645">
                  <ChevronRightIcon size={18} data-id="element-1646" />
                </button>
              </div>
            </div>}
        </div>}
      {/* Add Certificate Form */}
      {activeTab === 'add' && <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden" data-id="element-1647">
          <div className="p-6 border-b border-gray-200" data-id="element-1648">
            <h2 className="font-medium text-[#1A1F36]" data-id="element-1649">Add New Certificate</h2>
          </div>
          <form onSubmit={handleSubmit} className="p-6 space-y-6" data-id="element-1650">
            {/* BIC Code */}
            <div data-id="element-1651">
              <label htmlFor="bic" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1652">
                BIC Code
              </label>
              <input type="text" id="bic" value={bankInfo.code} disabled className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-700" data-id="element-1653" />
              <p className="mt-1 text-xs text-gray-500" data-id="element-1654">
                Bank Identifier Code (BIC) for {bankInfo.name}
              </p>
            </div>
            {/* Certificate */}
            <div data-id="element-1655">
              <label htmlFor="certificate" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1656">
                Certificate (PEM)
              </label>
              <div className="flex flex-col space-y-2" data-id="element-1657">
                <textarea id="certificate" value={certificateText} onChange={e => {
              setCertificateText(e.target.value);
              parseCertificate(e.target.value);
            }} placeholder="Paste PEM certificate here" className={`w-full px-3 py-2 border rounded-md min-h-[120px] font-mono text-sm ${errors.certificate ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-[#1A6FEF]'} focus:outline-none focus:ring-2 focus:border-transparent`} data-id="element-1658" />
                <div className="flex items-center" data-id="element-1659">
                  <label htmlFor="certificate-upload" className="px-3 py-2 bg-white border border-gray-300 text-gray-700 rounded-md font-medium hover:bg-gray-50 cursor-pointer flex items-center" data-id="element-1660">
                    <UploadIcon size={16} className="mr-2" data-id="element-1661" />
                    Upload Certificate
                    <input type="file" id="certificate-upload" className="hidden" accept=".pem,.crt,.cer" onChange={handleCertificateUpload} data-id="element-1662" />
                  </label>
                  <p className="ml-3 text-xs text-gray-500" data-id="element-1663">
                    Supported formats: .pem, .crt, .cer
                  </p>
                </div>
                {errors.certificate && <p className="text-sm text-red-600 flex items-center" data-id="element-1664">
                    <AlertCircleIcon size={14} className="mr-1" data-id="element-1665" />
                    {errors.certificate}
                  </p>}
              </div>
            </div>
            {/* Certificate Metadata */}
            {certificateMetadata && <div className="bg-gray-50 p-4 rounded-md border border-gray-200" data-id="element-1666">
                <h3 className="text-sm font-medium text-gray-700 mb-3" data-id="element-1667">
                  Certificate Details
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1668">
                  <div data-id="element-1669">
                    <p className="text-xs text-gray-500" data-id="element-1670">Subject</p>
                    <p className="text-sm font-medium" data-id="element-1671">
                      {certificateMetadata.subject}
                    </p>
                  </div>
                  <div data-id="element-1672">
                    <p className="text-xs text-gray-500" data-id="element-1673">Issuer</p>
                    <p className="text-sm font-medium" data-id="element-1674">
                      {certificateMetadata.issuer}
                    </p>
                  </div>
                  <div data-id="element-1675">
                    <p className="text-xs text-gray-500" data-id="element-1676">Serial Number</p>
                    <p className="text-sm font-medium font-mono" data-id="element-1677">
                      {certificateMetadata.serial}
                    </p>
                  </div>
                  <div data-id="element-1678">
                    <p className="text-xs text-gray-500" data-id="element-1679">Validity</p>
                    <p className="text-sm" data-id="element-1680">
                      <span className="font-medium" data-id="element-1681">
                        {certificateMetadata.validFrom}
                      </span>{' '}
                      to{' '}
                      <span className="font-medium" data-id="element-1682">
                        {certificateMetadata.validTo}
                      </span>
                      {certificateMetadata.isValid ? <span className="ml-2 text-green-600 text-xs font-medium flex items-center" data-id="element-1683">
                          <CheckCircleIcon size={12} className="mr-1" data-id="element-1684" />
                          Valid
                        </span> : <span className="ml-2 text-red-600 text-xs font-medium flex items-center" data-id="element-1685">
                          <XCircleIcon size={12} className="mr-1" data-id="element-1686" />
                          Invalid
                        </span>}
                    </p>
                  </div>
                </div>
              </div>}
            {/* Private Key */}
            <div data-id="element-1687">
              <label htmlFor="private-key" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1688">
                Private Key (Encrypted PKCS#8 PEM)
              </label>
              <div className="flex flex-col space-y-2" data-id="element-1689">
                <textarea id="private-key" value={privateKeyText} onChange={e => {
              setPrivateKeyText(e.target.value);
              if (!e.target.value.includes('-----BEGIN ENCRYPTED PRIVATE KEY-----')) {
                setErrors({
                  ...errors,
                  privateKey: 'Private key is unencrypted. Upload an encrypted PKCS#8 key.'
                });
              } else {
                const newErrors = {
                  ...errors
                };
                delete newErrors.privateKey;
                setErrors(newErrors);
              }
            }} placeholder="Paste encrypted PKCS#8 private key here" className={`w-full px-3 py-2 border rounded-md min-h-[120px] font-mono text-sm ${errors.privateKey ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-[#1A6FEF]'} focus:outline-none focus:ring-2 focus:border-transparent`} data-id="element-1690" />
                <div className="flex items-center" data-id="element-1691">
                  <label htmlFor="private-key-upload" className="px-3 py-2 bg-white border border-gray-300 text-gray-700 rounded-md font-medium hover:bg-gray-50 cursor-pointer flex items-center" data-id="element-1692">
                    <UploadIcon size={16} className="mr-2" data-id="element-1693" />
                    Upload Private Key
                    <input type="file" id="private-key-upload" className="hidden" accept=".pem,.key" onChange={handlePrivateKeyUpload} data-id="element-1694" />
                  </label>
                  <p className="ml-3 text-xs text-gray-500" data-id="element-1695">
                    Supported formats: .pem, .key
                  </p>
                </div>
                {errors.privateKey && <p className="text-sm text-red-600 flex items-center" data-id="element-1696">
                    <AlertCircleIcon size={14} className="mr-1" data-id="element-1697" />
                    {errors.privateKey}
                  </p>}
              </div>
            </div>
            {/* Private Key Password */}
            <div data-id="element-1698">
              <label htmlFor="private-key-password" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1699">
                Password for Private Key
              </label>
              <div className="relative" data-id="element-1700">
                <input type={showPrivateKeyPassword ? 'text' : 'password'} id="private-key-password" value={privateKeyPassword} onChange={e => setPrivateKeyPassword(e.target.value)} placeholder="Enter private key password" className={`w-full px-3 py-2 border rounded-md pr-10 ${errors.privateKeyPassword ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-[#1A6FEF]'} focus:outline-none focus:ring-2 focus:border-transparent`} data-id="element-1701" />
                <button type="button" className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700" onClick={() => setShowPrivateKeyPassword(!showPrivateKeyPassword)} data-id="element-1702">
                  {showPrivateKeyPassword ? <EyeOffIcon size={16} data-id="element-1703" /> : <EyeIcon size={16} data-id="element-1704" />}
                </button>
              </div>
              {errors.privateKeyPassword && <p className="mt-1 text-sm text-red-600 flex items-center" data-id="element-1705">
                  <AlertCircleIcon size={14} className="mr-1" data-id="element-1706" />
                  {errors.privateKeyPassword}
                </p>}
            </div>
            {/* Keystore Password */}
            <div data-id="element-1707">
              <label htmlFor="keystore-password" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1708">
                Set Keystore Password
              </label>
              <div className="relative" data-id="element-1709">
                <input type={showKeystorePassword ? 'text' : 'password'} id="keystore-password" value={keystorePassword} onChange={e => setKeystorePassword(e.target.value)} placeholder="Enter keystore password" className={`w-full px-3 py-2 border rounded-md pr-10 ${errors.keystorePassword ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-[#1A6FEF]'} focus:outline-none focus:ring-2 focus:border-transparent`} data-id="element-1710" />
                <button type="button" className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700" onClick={() => setShowKeystorePassword(!showKeystorePassword)} data-id="element-1711">
                  {showKeystorePassword ? <EyeOffIcon size={16} data-id="element-1712" /> : <EyeIcon size={16} data-id="element-1713" />}
                </button>
              </div>
              {errors.keystorePassword && <p className="mt-1 text-sm text-red-600 flex items-center" data-id="element-1714">
                  <AlertCircleIcon size={14} className="mr-1" data-id="element-1715" />
                  {errors.keystorePassword}
                </p>}
              <p className="mt-1 text-xs text-gray-500" data-id="element-1716">
                Password must be at least 8 characters long
              </p>
            </div>
            {/* Key Match Error */}
            {errors.keyMatch && <div className="bg-red-50 p-3 rounded-md border border-red-200" data-id="element-1717">
                <p className="text-sm text-red-600 flex items-center" data-id="element-1718">
                  <AlertCircleIcon size={14} className="mr-1" data-id="element-1719" />
                  {errors.keyMatch}
                </p>
              </div>}
            {/* Submit Button */}
            <div className="flex justify-end" data-id="element-1720">
              <button type="submit" disabled={!certificateText || !privateKeyText || !privateKeyPassword || !keystorePassword || Object.keys(errors).length > 0} className={`px-4 py-2 rounded-md font-medium ${!certificateText || !privateKeyText || !privateKeyPassword || !keystorePassword || Object.keys(errors).length > 0 ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-[#1A6FEF] text-white hover:bg-[#0F5ED6]'}`} data-id="element-1721">
                Add Certificate
              </button>
            </div>
          </form>
        </div>}
      {/* Deactivate Certificate Modal */}
      {showDeactivateModal && selectedCertificate && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-id="element-1722">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6" data-id="element-1723">
            <div className="flex items-center justify-between mb-4" data-id="element-1724">
              <h3 className="text-lg font-medium text-[#1A1F36]" data-id="element-1725">
                Confirm Deactivation
              </h3>
              <button onClick={() => setShowDeactivateModal(false)} className="text-gray-500 hover:text-gray-700" data-id="element-1726">
                <XIcon size={20} data-id="element-1727" />
              </button>
            </div>
            <div className="mb-6" data-id="element-1728">
              <p className="text-gray-700 mb-4" data-id="element-1729">
                Are you sure you want to deactivate the certificate for{' '}
                <strong data-id="element-1730">{selectedCertificate.participant}</strong> (
                {selectedCertificate.bic})?
              </p>
              <div className="bg-yellow-50 p-3 rounded-md border border-yellow-200" data-id="element-1731">
                <p className="text-sm text-yellow-800 flex items-center" data-id="element-1732">
                  <AlertCircleIcon size={14} className="mr-2 flex-shrink-0" data-id="element-1733" />
                  This action requires approval from a checker. The certificate
                  will remain active until approved.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-3" data-id="element-1734">
              <button onClick={() => setShowDeactivateModal(false)} className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-md font-medium hover:bg-gray-50" data-id="element-1735">
                Cancel
              </button>
              <button onClick={confirmStatusChange} className="px-4 py-2 bg-red-600 text-white rounded-md font-medium hover:bg-red-700" data-id="element-1736">
                Deactivate
              </button>
            </div>
          </div>
        </div>}
      {/* Activate Certificate Modal */}
      {showActivateModal && selectedCertificate && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-id="element-1737">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6" data-id="element-1738">
            <div className="flex items-center justify-between mb-4" data-id="element-1739">
              <h3 className="text-lg font-medium text-[#1A1F36]" data-id="element-1740">
                Confirm Activation
              </h3>
              <button onClick={() => setShowActivateModal(false)} className="text-gray-500 hover:text-gray-700" data-id="element-1741">
                <XIcon size={20} data-id="element-1742" />
              </button>
            </div>
            <div className="mb-6" data-id="element-1743">
              <p className="text-gray-700 mb-4" data-id="element-1744">
                Are you sure you want to activate the certificate for{' '}
                <strong data-id="element-1745">{selectedCertificate.participant}</strong> (
                {selectedCertificate.bic})?
              </p>
              <div className="bg-blue-50 p-3 rounded-md border border-blue-200" data-id="element-1746">
                <p className="text-sm text-blue-800 flex items-center" data-id="element-1747">
                  <AlertCircleIcon size={14} className="mr-2 flex-shrink-0" data-id="element-1748" />
                  This action requires approval from a checker. The certificate
                  will remain inactive until approved.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-3" data-id="element-1749">
              <button onClick={() => setShowActivateModal(false)} className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-md font-medium hover:bg-gray-50" data-id="element-1750">
                Cancel
              </button>
              <button onClick={confirmStatusChange} className="px-4 py-2 bg-green-600 text-white rounded-md font-medium hover:bg-green-700" data-id="element-1751">
                Activate
              </button>
            </div>
          </div>
        </div>}
    </div>;
}